import { camelCase, slugify } from '@sola/utils/string.ts';

import { keys } from './keys.ts';
import { articlesUrlBase } from './routes.ts';
import { programCountryLabels } from './sac-values.ts';
import type { ArticleTypesEnum } from './taxonomy-values.ts';
import {
  ArticleTermsEnum,
  articleCategoryOptionsMap,
} from './taxonomy-values.ts';

export const getArticlePath = (
  slug?: string | null,
  articleType?: ArticleTypesEnum | null,
): string => {
  if (!slug) {
    return '';
  }
  // This path does NOT have a trailing slash, so it matches the "path" property of a page.
  return `${articlesUrlBase[articleType || keys.articleTypes.blogPost]}/${slug}`;
};

/**
 * If `code` is a recognized 2-letter country ISO code, returns the
 * kebab-case slug for that country (e.g. "bd" → "bangladesh",
 * "BF" → "burkina-faso"). Returns undefined otherwise.
 *
 * Callers that always pass a known-valid country and want a slug back
 * regardless can fall back to slugifying the input:
 *   getCountrySlugFromCode(country) ?? slugify(country)
 */
export const getCountrySlugFromCode = (code: string): string | undefined => {
  if (!code) {
    return undefined;
  }
  const label =
    programCountryLabels[
      code.toUpperCase() as keyof typeof programCountryLabels
    ];
  if (!label) {
    return undefined;
  }
  return slugify(label);
};

export const getCountryCodeByTerm = (termStr: string): string | undefined => {
  return Object.keys(programCountryLabels).find(
    (key) =>
      slugify(
        programCountryLabels[key as keyof typeof programCountryLabels],
      ) === slugify(termStr),
  );
};

export const getTermCategory = (
  term: ArticleTermsEnum | undefined,
): keyof typeof articleCategoryOptionsMap | undefined => {
  const categories = Object.keys(articleCategoryOptionsMap) as Array<
    keyof typeof articleCategoryOptionsMap
  >;

  if (!term) {
    return undefined;
  }

  return categories.find((category) =>
    (
      articleCategoryOptionsMap[category] as ReadonlyArray<ArticleTermsEnum>
    ).includes(camelCase(term)),
  );
};

export const getCategoryTaxonomy = (category: string) => {
  let taxonomy = '';
  if (category === 'theme') {
    taxonomy = 'articleThemes';
  }
  if (category === 'topic') {
    taxonomy = 'articleTopics';
  }
  if (category === 'types') {
    taxonomy = 'articleTypes';
  }
  return taxonomy;
};

/**
 * Attempts to resolve a raw URL slug into a valid ArticleTermsEnum value.
 *
 * Resolution order:
 * 1. Direct match after camelCase (e.g. "child-development" → "childDevelopment")
 * 2. Returns undefined for anything that doesn't resolve — callers should 404.
 *
 * @NOTE Two-letter country ISO codes (e.g. "/blog/tag/bd") are NOT resolved
 * here. They are 301-redirected to their full country slug earlier in the
 * request by the `withCountryTagRedirect` proxy middleware, so by the time a
 * term reaches this function it is always a full slug. If that middleware is
 * ever removed, country-code URLs will stop resolving here.
 */
export const resolveArticleTerm = (
  term: string | undefined,
): ArticleTermsEnum | undefined => {
  if (!term) {
    return undefined;
  }

  // Try direct match (handles standard slugs like "child-development")
  const camelized = camelCase(term);
  const direct = ArticleTermsEnum.safeParse(camelized);
  if (direct.success) {
    return direct.data;
  }

  return undefined;
};
